import { Icon } from "matsuri-ui"
import { css } from "@emotion/react"
import { useCallback, useEffect, useRef, useState } from "react"
import styled from "@emotion/styled"
import React from "react"
import { imageCompress } from "@/utils/image"

const ImgSquareContainer = styled.div`
    position: relative;
    width: 100%;
    padding-top: 100%;
`

const Img = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`

const CloseButtonCover = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    margin: 5px;
    padding: 7px;
    background-color: #4a4a4a;
    border-radius: 50%;
    font-size: 11px;
    line-height: 1;
    cursor: pointer;

    & > svg {
        color: white;
    }
`

export interface PhotoUploaderThumbnailProps {
    compressing: boolean
    requested: boolean
    uploaded: boolean
    pictureKey?: string
    onClickPicture: (key: string) => void
}

export const PhotoUploaderThumbnail = ({
    pictureKey,
    compressing,
    requested,
    uploaded,
    onClickPicture,
    ...props
}: PhotoUploaderThumbnailProps & React.ImgHTMLAttributes<HTMLImageElement>) => {
    const handleClick = useCallback(() => {
        if (pictureKey) {
            onClickPicture(pictureKey)
        }
    }, [onClickPicture, pictureKey])

    return (
        <ImgSquareContainer onClick={handleClick}>
            <div
                data-state={
                    compressing
                        ? "compressing"
                        : !requested
                          ? "waitingInQueue"
                          : requested && !uploaded
                            ? "uploading"
                            : "uploaded"
                }
                role="button"
                css={css`
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    font-size: 32px;
                    transform: translate(-50%, -50%);

                    &[data-state="compressing"] {
                        background-color: rgba(128, 128, 128, 1);
                    }
                    &[data-state="waitingInQueue"] {
                        background-color: rgba(255, 255, 255, 0.7);
                    }
                    &[data-state="uploading"] {
                        background-color: rgba(65, 65, 65, 0.8);
                    }
                    &[data-state="uploaded"] {
                        cursor: pointer;
                        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
                    }
                `}
            >
                {requested && !uploaded && <>...</>}
                {uploaded && <Icon.Check color="success" />}
            </div>
            {uploaded && (
                <CloseButtonCover>
                    <Icon.Time />
                </CloseButtonCover>
            )}
            <Img {...props} />
        </ImgSquareContainer>
    )
}

const FileThumbnail = ({
    file,
    pictureKey,
    ...props
}: {
    file?: Blob
} & Omit<PhotoUploaderThumbnailProps, "compressing">) => {
    if (!file) {
        return (
            <PhotoUploaderThumbnail
                compressing={true}
                requested={false}
                uploaded={false}
                pictureKey={undefined}
                onClickPicture={() => {
                    return
                }}
            />
        )
    }

    const objectURL = URL.createObjectURL(file)
    const handleLoad = () => {
        URL.revokeObjectURL(objectURL)
    }

    return (
        <PhotoUploaderThumbnail
            {...props}
            src={objectURL}
            onLoad={handleLoad}
            compressing={!file}
            pictureKey={pictureKey}
        />
    )
}

const FileThumbnailAreaUnmemoized = ({
    compressedData,
    requested,
    uploaded,
    pictureKey,
    onClick
}: FileObject & { pictureKey: string; onClick: (key: string) => void }) => (
    <FileThumbnail
        file={compressedData}
        requested={requested}
        uploaded={uploaded}
        pictureKey={pictureKey}
        onClickPicture={onClick}
    />
)
const FileThumbnailArea = React.memo(FileThumbnailAreaUnmemoized)

// 最初にfileがセットされ、ファイルの圧縮処理が順次行われる。圧縮が終わったら圧縮後データがcompressedDataに保存された後、fileはundefinedになる(メモリ使用量を節約するため)
// その後、uploadが順次行われるという流れ
export interface FileObject {
    file?: File
    compressedData?: Blob
    key: string
    requested: boolean
    uploaded: boolean
}

const UPLOADING_THROTTLE = 5
const COMPRESSING_THROTTLE = 5

export const GridPhotoUploader = ({
    selectedFiles,
    requestUploadPicture,
    onClick,
    onCompleted,
    onChangeProgress
}: {
    selectedFiles?: File[]
    requestUploadPicture: (pictureKey: string, file: Blob) => Promise<void>
    onClick: (fileObject: FileObject, onDelete: () => void) => void
    onChangeProgress?: (progress: { total: number; uploaded: number }) => void
    onCompleted?: () => void
}) => {
    // fileにuniqueなkeyを付与するためのカウンター
    const counter = useRef<number>(0)
    // コンポーネント内で実行数を管理
    const compressingCount = useRef<number>(0)
    const uploadingCount = useRef<number>(0)

    // 対象となる写真を全て積む用
    // indexに依存したコードを書いているので削除したい時はそこをundefinedにし、順番を変えないようにするなどの工夫が必要
    const [pictures, setPictures] = useState<(FileObject | undefined)[]>([])
    // compress promiseを発火する副作用を起動する用のカウンター
    const [kickCompress, setKickCompress] = useState(0)
    // upload promiseを発火する副作用を起動する用のカウンター
    const [kickUpload, setKickUpload] = useState(0)

    const handleClick = useCallback(
        (key: string) => {
            const result = pictures
                .map((val, index) => [val, index] as const)
                .find(([p, _]) => p?.key === key)
            if (!result) return

            const [picture, index] = result
            if (!picture) return

            // アップロードがまだの写真は削除させない
            if (!picture.uploaded) return

            const onDelete = () => {
                setPictures(prev => {
                    const current = [...prev]
                    current[index] = undefined

                    // 削除時もChangeProgressを発火させる
                    if (onChangeProgress) {
                        onChangeProgress({
                            total: current.filter(p => p).length,
                            uploaded: current.filter(p => p?.uploaded).length
                        })
                    }
                    return current
                })
            }

            onClick(picture, onDelete)
        },
        [onChangeProgress, onClick, pictures]
    )

    useEffect(() => {
        void (() => {
            const files = selectedFiles
            if (!files) return

            // ひとまず初期状態のデータをpicturesに詰めておいて、圧縮が終わる間に何かしら画面に反映できるようにする
            const targets: FileObject[] = []
            for (const file of files) {
                counter.current++

                targets.push({
                    file,
                    key: `${counter.current}`,
                    requested: false,
                    uploaded: false
                })
            }

            setPictures(pictures => [...pictures, ...targets])
            setKickCompress(t => t + 1)
        })()
    }, [selectedFiles])

    useEffect(
        () => {
            const candidates = pictures
                .map((val, i) => [val, i] as const)
                .filter(([f, _]) => f && !f.compressedData)
                .slice(0, COMPRESSING_THROTTLE)

            for (const [fobj, index] of candidates) {
                // fobjがundefinedのパターンはfilterで消えているはずなので本来不要だが型がそこまで推論できないのでしょうがない
                if (!fobj) return
                if (compressingCount.current >= COMPRESSING_THROTTLE) break

                void (async () => {
                    compressingCount.current++
                    // cleaning evidenceは AWS Rekognition で解析するため JPEG に固定する。
                    const blob = await imageCompress(fobj.file!, {
                        mimeType: "image/jpeg",
                        maxWidth: 1024,
                        maxHeight: 1024
                    })
                    compressingCount.current--

                    // ここでインデックスアクセスをかけているので少なくともpromise実行中に順番が変更されないことが必要
                    setPictures(prev => {
                        if (!prev[index]) return prev
                        const current = [...prev]

                        // Fileの生データは不要なのでGCに回収してもらう
                        current[index]!.file = undefined
                        current[index]!.compressedData = blob

                        return current
                    })

                    // 1つ終わったら空きが出るはずなので次のものをkickする
                    setKickCompress(t => t + 1)
                    // 圧縮完了したので、アップロードをkickする
                    setKickUpload(t => t + 1)
                })()
            }
        },
        // kickUploadのフラグによりpromiseのキューイングの管理をしているのでpicturesは監視しない
        // (picturesをdepsに入れるとこのuseEffect内でsetPicturesしている関係で無限ループが起きる可能性があるため)

        [kickCompress]
    )

    useEffect(
        () => {
            // この実装では最大UPLOADING_THROTTLE個のものを待ちキューに積もうとしているが途中からは1つしか空きがない状態になる可能性が高いのでここのfilterは過剰にとってしまうかもしれない
            const candidates = pictures
                .map((val, i) => [val, i] as const)
                .filter(([f, _]) => f?.compressedData && !f.requested)
                .slice(0, UPLOADING_THROTTLE)

            for (const [fobj, index] of candidates) {
                if (!fobj) return
                if (uploadingCount.current >= UPLOADING_THROTTLE) break

                void (async () => {
                    setPictures(prev => {
                        const current = [...prev]
                        if (!current[index]) return current

                        current[index].requested = true

                        return current
                    })
                    uploadingCount.current++
                    await requestUploadPicture(fobj.key, fobj.compressedData!)
                    uploadingCount.current--

                    // ここでインデックスアクセスをかけているので少なくともpromise実行中に順番が変更されないことが必要
                    setPictures(prev => {
                        if (!prev[index]) return prev
                        const current = [...prev]

                        current[index]!.uploaded = true

                        return current
                    })

                    // 1つ終わったら空きが出るはずなので次のものをkickする
                    setKickUpload(t => t + 1)
                })()
            }
        },
        // kickUploadのフラグによりpromiseのキューイングの管理をしているのでpicturesは監視しない
        // (picturesをdepsに入れるとこのuseEffect内でsetPicturesしている関係で無限ループが起きる可能性があるため)

        [kickUpload, requestUploadPicture]
    )

    useEffect(() => {
        // 全てのファイルのアップロードが終わったらonCompletedを呼びたいので、アップロードが終わるたびに呼ばれるkickUploadに反応してcompletedかどうかを調べる
        // picturesが全部uploadedかどうかを調べるのは時間がかかりそうなので先にcompressingとuploadingの数をカウントしておく
        if (
            pictures.length > 0 &&
            compressingCount.current == 0 &&
            uploadingCount.current == 0 &&
            pictures.every(f => (f ? f.uploaded : true)) &&
            onCompleted
        ) {
            onCompleted()
        }

        // 進捗が変化したらonChangeProgressを呼ぶが、kickUploadがトリガーなので進捗が変化してなくても呼ばれてしまう可能性がある
        if (onChangeProgress) {
            onChangeProgress({
                total: pictures.filter(p => p).length,
                uploaded: pictures.filter(p => p?.uploaded).length
            })
        }
    }, [kickUpload])

    return (
        <div
            css={css`
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 16px;
            `}
        >
            {pictures.map(
                picture =>
                    picture && (
                        <FileThumbnailArea
                            {...picture}
                            key={picture.key}
                            pictureKey={picture.key}
                            onClick={handleClick}
                        />
                    )
            )}
        </div>
    )
}
